import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isDesktopBreakpoint, isMobileBreakpoint } from '@theme/utilities';

class PcpBrandHeaderComponent extends Component {
  /** @type {boolean} */
  #isBrandDropdownOpen = false;

  /** @type {boolean} */
  #isNavDrawerOpen = false;

  /** @type {boolean} */
  #isBodyScrollLocked = false;

  /** @type {string} */
  #previousBodyOverflow = '';

  /** @type {number|null} */
  #hideDropdownTimeout = null;

  /** @type {Map<HTMLElement, number>} Per-container debounce timers for nav dropdowns */
  #navDropdownTimeouts = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#initBrandDropdown();
    this.#initNavDropdowns();
    this.#initNavAccordions();
    this.#initNavDrawer();

    document.addEventListener('click', this.#handleDocumentClick);
    document.addEventListener('keydown', this.#handleKeyDown);
    window.addEventListener('resize', this.#handleViewportChange);
    window.visualViewport?.addEventListener('resize', this.#handleViewportChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.removeEventListener('resize', this.#handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.#handleViewportChange);
    this.#cancelHideDropdown();
    this.#navDropdownTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#navDropdownTimeouts.clear();
    this.#unlockBodyScroll();
    removeTrapFocus();
  }

  #initBrandDropdown() {
    this.querySelectorAll('[data-pcp-brand-trigger]').forEach((trigger) => {
      // Mobile/touch: tap to toggle (hover doesn't exist on touch devices).
      trigger.addEventListener('click', (event) => {
        if (isDesktopBreakpoint()) return;
        event.stopPropagation();
        this.#toggleBrandDropdown();
      });

      // Desktop: open on hover, mirroring the global sub-header overlay. Hover-close
      // releases the body-scroll lock the moment the pointer leaves the brand/dropdown,
      // so the page below stays scrollable — the click version kept it locked (QA bug).
      trigger.addEventListener('mouseenter', () => {
        if (isMobileBreakpoint()) return;
        this.#cancelHideDropdown();
        this.#openBrandDropdown();
      });

      trigger.addEventListener('mouseleave', () => {
        if (isMobileBreakpoint()) return;
        this.#scheduleHideDropdown();
      });
    });

    // Keep the dropdown open while the pointer is over it (bridges the gap from the
    // trigger); close shortly after it leaves.
    const dropdown = this.querySelector('[data-pcp-brand-dropdown]');
    if (dropdown instanceof HTMLElement) {
      dropdown.addEventListener('mouseenter', () => {
        if (isMobileBreakpoint()) return;
        this.#cancelHideDropdown();
      });
      dropdown.addEventListener('mouseleave', () => {
        if (isMobileBreakpoint()) return;
        this.#scheduleHideDropdown();
      });
    }
  }

  #scheduleHideDropdown() {
    this.#cancelHideDropdown();
    this.#hideDropdownTimeout = window.setTimeout(() => {
      this.#hideDropdownTimeout = null;
      this.#closeBrandDropdown();
    }, 100);
  }

  #cancelHideDropdown() {
    if (this.#hideDropdownTimeout) {
      clearTimeout(this.#hideDropdownTimeout);
      this.#hideDropdownTimeout = null;
    }
  }

  #initNavDropdowns() {
    this.querySelectorAll('[data-pcp-nav-dropdown-container]').forEach((container) => {
      container.addEventListener('mouseenter', () => {
        if (isMobileBreakpoint()) return;
        this.#cancelHideNavDropdown(container);
        this.#openNavDropdown(container);
      });

      container.addEventListener('mouseleave', () => {
        if (isMobileBreakpoint()) return;
        this.#scheduleHideNavDropdown(container);
      });

      // Bridge pattern: keep dropdown open while pointer is over the panel itself
      const dropdown = container.querySelector('[data-pcp-nav-dropdown]');
      if (dropdown instanceof HTMLElement) {
        dropdown.addEventListener('mouseenter', () => {
          if (isMobileBreakpoint()) return;
          this.#cancelHideNavDropdown(container);
        });

        dropdown.addEventListener('mouseleave', () => {
          if (isMobileBreakpoint()) return;
          this.#scheduleHideNavDropdown(container);
        });
      }
    });
  }

  #initNavAccordions() {
    this.querySelectorAll('[data-pcp-nav-accordion-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const accordion = trigger.closest('[data-pcp-nav-accordion]');
        if (!accordion) return;

        const content = accordion.querySelector('[data-pcp-nav-accordion-content]');
        if (!(content instanceof HTMLElement)) return;

        const isOpen = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        content.classList.toggle('tw:hidden', isOpen);
        content.classList.toggle('tw:flex', !isOpen);
      });
    });
  }

  /** @param {HTMLElement} container */
  #openNavDropdown(container) {
    // Close any other open nav dropdown
    this.querySelectorAll('[data-pcp-nav-dropdown-container]').forEach((other) => {
      if (other !== container) {
        this.#cancelHideNavDropdown(other);
        this.#closeNavDropdown(other);
      }
    });

    const dropdown = container.querySelector('[data-pcp-nav-dropdown]');
    const trigger = container.querySelector('[data-pcp-nav-dropdown-trigger]');
    if (!(dropdown instanceof HTMLElement)) return;

    dropdown.classList.remove('tw:invisible', 'tw:-translate-y-1', 'tw:opacity-0', 'tw:pointer-events-none');
    dropdown.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  /** @param {HTMLElement} container */
  #closeNavDropdown(container) {
    const dropdown = container.querySelector('[data-pcp-nav-dropdown]');
    const trigger = container.querySelector('[data-pcp-nav-dropdown-trigger]');
    if (!(dropdown instanceof HTMLElement)) return;

    dropdown.classList.add('tw:invisible', 'tw:-translate-y-1', 'tw:opacity-0', 'tw:pointer-events-none');
    dropdown.setAttribute('aria-hidden', 'true');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  #closeAllNavDropdowns() {
    this.querySelectorAll('[data-pcp-nav-dropdown-container]').forEach((container) => {
      this.#cancelHideNavDropdown(container);
      this.#closeNavDropdown(container);
    });
  }

  /** @param {HTMLElement} container */
  #scheduleHideNavDropdown(container) {
    this.#cancelHideNavDropdown(container);
    const timeoutId = window.setTimeout(() => {
      this.#navDropdownTimeouts.delete(container);
      this.#closeNavDropdown(container);
    }, 100);
    this.#navDropdownTimeouts.set(container, timeoutId);
  }

  /** @param {HTMLElement} container */
  #cancelHideNavDropdown(container) {
    const timeoutId = this.#navDropdownTimeouts.get(container);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.#navDropdownTimeouts.delete(container);
    }
  }

  #initNavDrawer() {
    document.querySelectorAll('[data-nav-drawer-open]').forEach((button) => {
      button.addEventListener('click', this.#openNavDrawer);
    });

    this.querySelector('[data-pcp-nav-drawer-close]')?.addEventListener('click', this.#closeNavDrawer);
  }

  #toggleBrandDropdown() {
    if (this.#isBrandDropdownOpen) {
      this.#closeBrandDropdown();
    } else {
      this.#openBrandDropdown();
    }
  }

  #openBrandDropdown() {
    const dropdown = this.querySelector('[data-pcp-brand-dropdown]');
    if (!(dropdown instanceof HTMLElement)) return;

    this.#closeAllNavDropdowns();
    this.#positionDropdown(dropdown);
    dropdown.classList.remove('tw:invisible', 'tw:-translate-y-2', 'tw:opacity-0', 'tw:pointer-events-none');
    dropdown.setAttribute('aria-hidden', 'false');

    this.querySelectorAll('[data-pcp-brand-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
    });

    // Lock the page behind the open dropdown (like the global sub-header overlay) so it
    // can't scroll away underneath — this is what keeps the fixed dropdown pinned to the
    // header instead of "sticking to the top", and stops the mobile scroll-chaining.
    this.#lockBodyScroll();
    this.#isBrandDropdownOpen = true;
  }

  /**
   * Pin the dropdown directly under the active header (desktop nav / mobile bar). On
   * mobile, cap its height to the space actually visible above the browser chrome
   * (Safari toolbar / theme-preview bar) via the visual viewport, so the last brands stay
   * reachable and the list scrolls internally instead of being clipped off-screen.
   * @param {HTMLElement} dropdown
   */
  #positionDropdown(dropdown) {
    const mobileHeader = this.querySelector('.tw\\:lg\\:hidden');
    const desktopHeader = this.querySelector('nav');
    const isDesktop = isDesktopBreakpoint();
    const activeHeader =
      isDesktop && desktopHeader instanceof HTMLElement
        ? desktopHeader
        : mobileHeader instanceof HTMLElement
          ? mobileHeader
          : this;

    const top = activeHeader.getBoundingClientRect().bottom;
    dropdown.style.top = `${top}px`;

    if (isDesktop) {
      // Desktop lays all brands out in a wrapping row (overflow visible) — no height cap.
      dropdown.style.maxHeight = '';
      return;
    }

    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const BOTTOM_GAP = 16;
    dropdown.style.maxHeight = `${Math.max(viewportHeight - top - BOTTOM_GAP, 0)}px`;
  }

  #closeBrandDropdown() {
    const dropdown = this.querySelector('[data-pcp-brand-dropdown]');
    if (!(dropdown instanceof HTMLElement)) return;

    dropdown.classList.add('tw:invisible', 'tw:-translate-y-2', 'tw:opacity-0', 'tw:pointer-events-none');
    dropdown.setAttribute('aria-hidden', 'true');

    this.querySelectorAll('[data-pcp-brand-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.#unlockBodyScroll();
    this.#isBrandDropdownOpen = false;
  }

  /** Keep the open dropdown aligned + correctly sized when the visual viewport changes
   *  (mobile browser chrome showing/hiding, orientation change). */
  #handleViewportChange = () => {
    if (isMobileBreakpoint()) {
      this.#closeAllNavDropdowns();
    }

    if (!this.#isBrandDropdownOpen) return;

    const dropdown = this.querySelector('[data-pcp-brand-dropdown]');
    if (dropdown instanceof HTMLElement) this.#positionDropdown(dropdown);
  };

  #openNavDrawer = () => {
    const drawer = this.querySelector('[data-pcp-nav-drawer]');
    if (!(drawer instanceof HTMLElement) || isDesktopBreakpoint()) return;

    this.#closeBrandDropdown();
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.remove('tw:invisible', 'tw:-translate-x-full', 'tw:opacity-0', 'tw:pointer-events-none');
    this.style.zIndex = '10000';
    this.#lockBodyScroll();
    this.#isNavDrawerOpen = true;

    requestAnimationFrame(() => trapFocus(drawer));
  };

  #closeNavDrawer = () => {
    const drawer = this.querySelector('[data-pcp-nav-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    drawer.setAttribute('aria-hidden', 'true');
    drawer.classList.add('tw:invisible', 'tw:-translate-x-full', 'tw:opacity-0', 'tw:pointer-events-none');
    this.style.removeProperty('z-index');
    this.#unlockBodyScroll();
    this.#isNavDrawerOpen = false;
    removeTrapFocus();
  };

  #lockBodyScroll() {
    if (this.#isBodyScrollLocked) return;

    this.#previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#isBodyScrollLocked = true;
  }

  #unlockBodyScroll() {
    if (!this.#isBodyScrollLocked) return;

    document.body.style.overflow = this.#previousBodyOverflow;
    this.#previousBodyOverflow = '';
    this.#isBodyScrollLocked = false;
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;

    if (this.#isBrandDropdownOpen && !this.contains(event.target)) {
      this.#closeBrandDropdown();
    }

    // Close any nav dropdown whose container doesn't contain the click target
    this.querySelectorAll('[data-pcp-nav-dropdown-container]').forEach((container) => {
      if (!container.contains(event.target)) {
        this.#cancelHideNavDropdown(container);
        this.#closeNavDropdown(container);
      }
    });
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (this.#isBrandDropdownOpen) {
      this.#closeBrandDropdown();
    }

    this.#closeAllNavDropdowns();

    if (this.#isNavDrawerOpen) {
      this.#closeNavDrawer();
    }
  };
}

if (!customElements.get('pcp-brand-header')) {
  customElements.define('pcp-brand-header', PcpBrandHeaderComponent);
}
